import { useMemo, useState } from "react";

import { Dropdown, Slider } from "@/components";
import { Ellipsis } from "@/icons";
import { CollectionItem, FullItems, ItemType, Playlist } from "@/api";
import { useTranslate } from "@/providers/context";

import { DropdownOption } from "@/components/Forms/Dropdown.tsx";

interface PlaylistItem {
  ChildId: string;
  Type: ItemType;
  Position: number;
}
interface PlaylistSliderProps {
  playlist: Playlist;
  items?: PlaylistItem[];
  dropdownOptions: DropdownOption[];
  idx: number;
  fullItems: FullItems;
}

export const PlaylistSlider = ({
  items,
  playlist,
  dropdownOptions,
  fullItems,
  idx,
}: PlaylistSliderProps) => {
  const { translate } = useTranslate();
  const [hovered, setHovered] = useState(false);

  // Make clicking an option disable the hover to avoid hover being maintained
  const options = useMemo(
    () =>
      dropdownOptions?.map((opt) => ({
        ...opt,
        onClick: () => {
          opt.onClick();
          setHovered(false);
        },
      })),
    [dropdownOptions, setHovered],
  );

  if (!items || items.length === 0) {
    // No items in playlist
    return (
      <div
        key={playlist.PlaylistId}
        className="mb-10"
        onMouseOver={() => setHovered(true)}
        onMouseOut={() => setHovered(false)}
      >
        <div
          data-testid={`playlist-title-${idx}`}
          className="h5-bold text-item-primary mb-[26px] flex items-center justify-between overflow-hidden overflow-ellipsis whitespace-nowrap"
        >
          <h5>{playlist.PlaylistName}</h5>
          {hovered && (
            <Dropdown options={dropdownOptions} contentProps={{ align: "end" }}>
              <Ellipsis className="focus-visible:outline-item-secondary h-6 w-6" />
            </Dropdown>
          )}
        </div>
        <div className="p2-medium text-item-primary flex h-[22px] items-center capitalize">
          {translate("No items")}
        </div>
      </div>
    );
  }
  return (
    <Slider
      className="mb-10"
      playlistId={playlist.PlaylistId}
      fullItems={fullItems}
      collection={{
        Items: items as unknown as CollectionItem[],
        TabId: playlist.PlaylistId,
        Name: playlist.PlaylistName,
      }}
      dropdownOptions={hovered ? options : undefined}
      onMouseOver={() => setHovered(true)}
      onMouseOut={() => setHovered(false)}
    />
  );
};
